import React, { useState } from "react";
import { useSnackbar } from "notistack";
import { showSnack } from "../../../utils/component";
import { LOG_LEVELS } from "../../../constants";
import {
	QueueLogLevelChangeRequest,
	ServiceLogLevelResponse,
	UpdateServiceLogLevelResponse,
} from "../../../models/Administration";
import { ServiceLogLevelDisplayCard } from "./ServiceLogLevelDisplayCard";
import { Button, CircularProgress } from "@material-ui/core";
import { updateServiceLogLevels } from "../../../services/api/serviceLogLevel.api";
export interface ServiceLogLevelDisplayFormProps {
	// The services retrieved
	services: ServiceLogLevelResponse[];
}

/**
 * Form for displaying and editing the service logging levels
 */
export const ServiceLogLevelDisplayForm = ({ services }: ServiceLogLevelDisplayFormProps) => {
	const { enqueueSnackbar } = useSnackbar();
	const [retrievedServicesList, setRetrievedServicesList] = useState<ServiceLogLevelResponse[]>(
		services.map((x) => ({ ...x })),
	);
	const [servicesList, setServicesList] = useState<ServiceLogLevelResponse[]>(services.map((x) => ({ ...x })));
	const [updateServiceList, setUpdateServiceList] = useState<QueueLogLevelChangeRequest[]>([]);
	const [update, setUpdate] = useState<boolean>(false);
	const [updating, setUpdating] = useState<boolean>(false);

	React.useEffect(() => {
		const updateLogLevels = async () => {
			try {
				const { responses, userName } = (await updateServiceLogLevels(updateServiceList)()).data;
				const responseList: UpdateServiceLogLevelResponse[] = JSON.parse(responses);
				const failures: string[] = [];
				const newUpdateList = [...updateServiceList];
				responseList.forEach((result) => {
					if (!result.success) {
						failures.push(result.serviceName);
					} else {
						newUpdateList.splice(
							newUpdateList.findIndex((x) => x.targetService === result.serviceName),
							1,
						);
					}
				});
				if (failures.length > 0) {
					showSnack(enqueueSnackbar, "Some Services Failed to Update.", "error");
				} else {
					showSnack(enqueueSnackbar, "Successfully Queued All Log Level Updates.", "success");
				}
				setRetrievedServicesList(
					servicesList.map((service, index) => {
						if (!failures.find((x) => x === service.serviceName))
							return { ...service, updatedBy: userName, updatedDate: new Date() };
						else return retrievedServicesList[index];
					}),
				);
				setUpdateServiceList(newUpdateList);
			} catch (ex) {
				console.log(ex);
				showSnack(enqueueSnackbar, "An Error Occurred", "error");
			} finally {
				setUpdate(false);
				setUpdating(false);
			}
		};
		if (update && !updating) {
			setUpdating(true);
			updateLogLevels();
		}
	}, [updating, update, updateServiceList, servicesList, enqueueSnackbar, retrievedServicesList]);

	const updateService = (serviceName: string, logLevel: string) => {
		const newList = [...updateServiceList];
		const existsIndex = newList.findIndex((x) => x.targetService == serviceName);
		if (existsIndex > -1) {
			newList[existsIndex].threshold = LOG_LEVELS[logLevel];
		} else {
			newList.push({ threshold: LOG_LEVELS[logLevel], targetService: serviceName });
		}
		setUpdateServiceList(newList);

		const newServiceList = [...servicesList];
		newServiceList[servicesList.findIndex((x) => x.serviceName === serviceName)].logLevel = logLevel;
	};

	return (
		<div>
			<div>
				{servicesList.map((service, index) => {
					return (
						<div key={service.serviceName}>
							<ServiceLogLevelDisplayCard
								service={service}
								setLogLevel={updateService}
								updated={service.logLevel !== retrievedServicesList[index].logLevel}
							></ServiceLogLevelDisplayCard>
						</div>
					);
				})}
			</div>
			<div style={{ marginBottom: "20px" }}>
				<div style={{ float: "right", marginRight: "15px" }}>
					{updating ? (
						<CircularProgress data-testid="fetcher-loading-indicator" />
					) : (
						<>
							<Button
								onClick={() => {
									setUpdate(true);
								}}
								disabled={updateServiceList.length === 0}
							>
								Update
							</Button>
							<Button
								onClick={() => {
									setUpdateServiceList([]);
									console.log(retrievedServicesList);
									setServicesList(retrievedServicesList.map((x) => ({ ...x })));
								}}
								disabled={updateServiceList.length === 0}
							>
								Cancel
							</Button>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
